import moment from 'moment';
import * as Yup from 'yup';
import { RegularExpressions } from '../../../../../../global/constants/regular-expressions';
import { createNewBeneficiary } from '../../../../../../global/ducks/benadmin-api';
import { replaceAll } from '../../../../../../global/helpers/string-helpers';
import { getRequiredText } from '../../../../../../global/helpers/transformer';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { KeyValuePair } from '../../../../models/arc-epi-props';
import { BeneficiaryCreateFormModel, BeneficiaryOrganizationType, BeneficiaryType, RelationshipOrganization, RelationshipOrganizationRequest, RelationshipPerson, RelationshipPersonRequest, RelationshipType } from '../../model/beneficiary-create-models';
import { BeneficiarySettings } from '../../model/epi-props';
import { getAreaCodeAndNumber } from '../../../../../../global/helpers/form-data-helper';
import { PhoneValidationSchema, PhoneCountryCodeValidationSchema } from '../../../../../../global/validation-schemas/phone-number-form-validation';
import { PersonPhoneValues } from '../../../../../../global/models/person-model';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { address_1_validation, address_2_validation, address_3_validation, city_validation, postal_code_validation, state_validation } from '../../../../../../global/validation-schemas/address-form-validation';
import { apiErrorClear } from '../../../../../../global/ducks/base-api';

export function getInitialFormModel(): BeneficiaryCreateFormModel {
	return {
		beneficiary_type: BeneficiaryType.Person,
		relationship: null,
		first_name: '',
		middle_name: '',
		last_name: '',
		suffix: '',
		perferred_name: '',
		gender: '',
		date_of_birth: null,
		tax_id: '',
		mailing_country: '',
		mailing_address_1: '',
		mailing_address_2: '',
		mailing_address_3: '',
		mailing_city: '',
		mailing_state: '',
		mailing_postal_code: '',
		business_phone_country_code: '',
		business_phone_area_code: '',
		business_phone_number: '',
		business_phone_formated: '',
		business_phone_extension: '',
		home_phone_country_code: '',
		home_phone_area_code: '',
		home_phone_number: '',
		home_phone_formated: '',
		home_phone_extension: '',
		mobile_phone_country_code: '',
		mobile_phone_area_code: '',
		mobile_phone_number: '',
		mobile_phone_formated: '',
		mobile_phone_extension: '',
		office_email_address: '',
		personal_email_address: '',
		same_address: false,
		org_name: '',
		contact_first_name: '',
		contact_last_name: '',
		contact_phone: '',
		contact_email: '',
		acknowledge: false,
		designation_salutation: null,
		disabled_child_indicator: null,
		gender_identity: null,
		gender_identity_other: null,
	};
}

const relationshipOptions = [
	{
		text: RelationshipType.Child,
		value: RelationshipType.Child,
	},
	{
		text: RelationshipType.StepChild,
		value: RelationshipType.StepChild,
	},
	{
		text: RelationshipType.Father,
		value: RelationshipType.Father,
	},
	{
		text: RelationshipType.Mother,
		value: RelationshipType.Mother,
	},
	{
		text: RelationshipType.Spouse,
		value: RelationshipType.Spouse,
	},
	{
		text: RelationshipType.Domestic,
		value: RelationshipType.Domestic,
	},
	{
		text: RelationshipType.Other,
		value: RelationshipType.Other,
	},
];

const relationshipValues = [
	...relationshipOptions,
	{
		text: RelationshipType.FormerSpouse,
		value: RelationshipType.FormerSpouse,
	},
];

function getMappedValue(value: string, defaultValue: string, mappings: KeyValuePair[]): string {

	if (value && mappings?.length > 0) {
		const mappedValue = mappings.find(m => m.key?.toLowerCase() === value?.toLowerCase());

		return mappedValue?.value || defaultValue;
	}

	return defaultValue;
}

export function GetMappedRelationship(options: KeyValuePair[]) {
	return relationshipValues?.map(a => { return { text: getMappedValue(a?.value, a?.text, options), value: a?.value }; });
}

export function GetMappedRelationshipOptions(options: KeyValuePair[]) {
	return relationshipOptions?.map(a => { return { text: getMappedValue(a?.value, a?.text, options), value: a?.value }; });
}

export function GetBeneficiaryTypeOptions(settings: BeneficiarySettings) {

	return [
		{
			label: settings?.personOptionText,
			value: BeneficiaryType.Person,
		},
		{
			label: settings?.entityOptionText,
			value: BeneficiaryType.Entity,
		},
		{
			label: settings?.estateOptionText,
			value: BeneficiaryType.Estate,
		},
		{
			label: settings?.trustOptionText,
			value: BeneficiaryType.Trust,
		},

	];

}

export function GetOrgFormContentModel(type: BeneficiaryType, settings: BeneficiarySettings) {
	switch (type) {
		case BeneficiaryType.Estate:
			return {
				title: settings?.estateTitle,
				description: settings?.estateDescription,
				legalNameLabel: settings?.legalEstateNameLabel,
				legalNameHelperText: settings?.legalEstateNameHelperText,
				personTitle: settings?.executorTitle,
			};
		case BeneficiaryType.Entity:
			return {
				title: settings?.entityTitle,
				description: settings?.entityDescription,
				legalNameLabel: settings?.legalEntityNameLabel,
				legalNameHelperText: settings?.legalEntityNameHelperText,
				personTitle: settings?.contactPersonTitle,
			};
		case BeneficiaryType.Trust:
			return {
				title: settings?.trustTitle,
				description: settings?.trustDescription,
				legalNameLabel: settings?.legalTrustNameLabel,
				legalNameHelperText: settings?.legalTrustNameHelperText,
				personTitle: settings?.trusteeTitle,
			};
		default:
			return {
				title: settings?.estateTitle,
				description: settings?.estateDescription,
				legalNameLabel: settings?.legalEstateNameLabel,
				legalNameHelperText: settings?.legalEstateNameHelperText,
				personTitle: settings?.executorTitle,
			};
	}
}

export function CreateBeneficiary(formData: BeneficiaryCreateFormModel, dispatch: any, guid: string, onSuccess: any) {
	let payload: any;

	const parsed = {
		...formData,
	};

	if (parsed?.beneficiary_type === BeneficiaryType.Person) {
		const mp = getAreaCodeAndNumber(parsed.mobile_phone_country_code, parsed.mobile_phone_formated);
		parsed.mobile_phone_area_code = mp.areaCode;
		parsed.mobile_phone_number = mp.phone;

		const hp = getAreaCodeAndNumber(parsed.home_phone_country_code, parsed.home_phone_formated);
		parsed.home_phone_area_code = hp.areaCode;
		parsed.home_phone_number = hp.phone;

		const payloadPerson: RelationshipPerson = {
			relationship_code: parsed?.relationship,
			object_person_attributes: {
				designation_salutation: parsed.designation_salutation,
				first_name: parsed?.first_name,
				middle_name: parsed?.middle_name,
				last_name: parsed?.last_name,
				preferred_name: parsed?.perferred_name,
				gender: parsed?.gender,
				date_of_birth: parsed?.date_of_birth,
				ssn: parsed?.tax_id,
				suffix: parsed?.suffix,
				disabled_child_indicator: parsed?.disabled_child_indicator === YesNoIndicators.Yes ? 'Y' : 'N',
				mailing_address_attributes: {
					country: parsed?.mailing_country,
					address1: parsed?.mailing_address_1,
					address2: parsed?.mailing_address_2,
					address3: parsed?.mailing_address_3,
					city: parsed?.mailing_city,
					state: parsed?.mailing_state,
					postal_code: parsed?.mailing_postal_code,
					o_location_id: null,
				},
				mobile_number_attributes: {
					phone_area_code: parsed?.mobile_phone_area_code,
					phone_country_code: parsed?.mobile_phone_country_code,
					phone_number: parsed?.mobile_phone_number,
					phone_extension: parsed?.mobile_phone_extension,
				},
				home_number_attributes: {
					phone_area_code: parsed?.home_phone_area_code,
					phone_country_code: parsed?.home_phone_country_code,
					phone_number: parsed?.home_phone_number,
					phone_extension: parsed?.home_phone_extension,
				},
				personal_email_attributes: {
					email_address: parsed?.personal_email_address,
				},
			},
		};

		const payloadPersonRequest: RelationshipPersonRequest = {
			relationship: payloadPerson,
		};

		payload = payloadPersonRequest;
	}

	if (parsed?.beneficiary_type !== BeneficiaryType.Person) {

		const payloadRelationship: RelationshipOrganization =
		{
			relationship_code: RelationshipType.Other,
			object_organization_attributes: {
				report_name: parsed?.org_name,
				tax_id: parsed?.tax_id,
				business_address_attributes: {
					country: parsed?.mailing_country,
					address1: parsed?.mailing_address_1,
					address2: parsed?.mailing_address_2,
					address3: parsed?.mailing_country === 'US' ? '' : parsed?.mailing_address_3,
					city: parsed?.mailing_city,
					state: parsed?.mailing_state,
					postal_code: parsed?.mailing_postal_code,
				},
			},
		};

		const beneficiaryOrganizationType: BeneficiaryOrganizationType = {
			organization_type: parsed?.beneficiary_type,
			contact_person: `${parsed?.contact_first_name} ${parsed?.contact_last_name}`,
			contact_phone: parsed?.contact_phone,
			contact_email: parsed?.contact_email,
		};

		const payloadOrganization: RelationshipOrganizationRequest = {
			relationship: payloadRelationship,
			beneficiary_organization_type: beneficiaryOrganizationType,
		};

		payload = payloadOrganization;
	}
  dispatch(apiErrorClear());
	dispatch(createNewBeneficiary(guid, payload, onSuccess));
}

function GetOrgNameLabel(type: BeneficiaryType, settings: BeneficiarySettings) {
	switch (type) {
		case BeneficiaryType.Estate:
			return settings?.legalEstateNameLabel;
		case BeneficiaryType.Entity:
			return settings?.legalEntityNameLabel;
		case BeneficiaryType.Trust:
			return settings?.legalTrustNameLabel;
		default:
			return settings?.legalEstateNameLabel;
	}
}

export function GetValidationSchemaForCreateBeneficiary(settings: BeneficiarySettings, labelSettings: LabelSettingProps) {
	const addressType = '';

	return Yup.lazy((
		{
			beneficiary_type,
			mobile_phone_formated,
			mobile_phone_country_code,
			home_phone_formated,
			home_phone_country_code,
			mailing_country,
			mailing_address_1,
			mailing_address_2,
			mailing_address_3,
			mailing_city,
			mailing_state,
			mailing_postal_code,
		}) => {

		const personPhoneValues: PersonPhoneValues = {
			mobile_phone_formated,
			mobile_phone_country_code,
			home_phone_formated,
			home_phone_country_code,
		};

		// Check if any address field has been entered
		const hasAnyAddressEntry = !!(
			mailing_country || 
			mailing_address_1 || 
			mailing_address_2 || 
			mailing_address_3 || 
			mailing_city || 
			mailing_state || 
			mailing_postal_code
		);

		// For US, MX, CA: address1, country, city, state, zip are required
		// For other countries: only address1 and country are required
		const isUSMXCA = mailing_country === 'US' || mailing_country === 'MX' || mailing_country === 'CA';
		const isPerson = beneficiary_type === BeneficiaryType.Person;

		// Check if address 2 or 3 is filled (requiring preceding address lines)
		const hasAddress2 = !!(mailing_address_2 && mailing_address_2.trim());
		const hasAddress3 = !!(mailing_address_3 && mailing_address_3.trim());

		// Address 1 is required if any address field is entered OR if address 2/3 is filled
		const requireAddress1 = hasAnyAddressEntry || hasAddress2 || hasAddress3;
		// Address 2 is required if address 3 is filled
		const requireAddress2 = hasAddress3;

		// Pre-compute address validation schemas to avoid nested ternaries
		let mailingCountryValidation = Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, labelSettings?.countryLabel));
		if (isPerson && !hasAnyAddressEntry) {
			mailingCountryValidation = Yup.string().nullable();
		}

		let mailingAddress1Validation = address_1_validation(labelSettings, addressType);
		if (isPerson && !requireAddress1) {
			mailingAddress1Validation = address_3_validation(labelSettings);
		}

		// Address 2 validation - required if address 3 is filled
		const address2RequiredMessage = getRequiredText(labelSettings?.requiredFormat, labelSettings.addressLabel2);
		let mailingAddress2Validation = address_2_validation(labelSettings);
		if (isPerson && requireAddress2) {
			// Create a validation that requires address 2 when address 3 is filled
			mailingAddress2Validation = Yup.string().nullable()
				.matches(/^[a-zA-ZÀ-ÖØ-öø-ÿ0-9#/\-' ]*$/,
					labelSettings.specialCharactersError || 'No special characters are allowed')
				.matches(/^(?!.*\s{2,})(?!\s)(?!.*\s$).*$/,
					'No leading, trailing, or consecutive spaces allowed')
				.test(
					'contains-letter',
					labelSettings.mustContainAlphabeticalErrorMessage || 'Address must contain alphabetic characters',
					value => !value || /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(value),
				)
				.max(40, labelSettings.maxCharactersError?.replace('{max}', '40') || 'Maximum 40 characters')
				.test(
					'empty-or-2-characters-check',
					labelSettings.min2CharacterErrorMessage,
					value => !value || value?.replace(/\s+/g, '')?.length >= 2,
				)
				.required(address2RequiredMessage);
		}

		const optionalCityValidation = Yup.string().nullable()
			.matches(/^[a-zA-ZÀ-ÖØ-öø-ÿ \-']*$/,
				labelSettings.specialCharactersError || 'No special characters are allowed')
			.matches(/^(?!.*\s{2,})(?!\s)(?!.*\s$).*$/,
				'No leading, trailing, or consecutive spaces allowed')
			.test(
				'contains-letter',
				labelSettings.mustContainAlphabeticalErrorMessage || 'City must contain alphabetic characters',
				value => !value || /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(value),
			)
			.max(40, labelSettings.maxCharactersError?.replace('{max}', '40') || 'Maximum 40 characters')
			.test(
				'empty-or-2-characters-check',
				labelSettings.min2CharacterErrorMessage,
				value => !value || value?.replace(/\s+/g, '')?.length >= 2,
			);

		let mailingCityValidation = city_validation(labelSettings, addressType);
		if (isPerson && !(hasAnyAddressEntry && isUSMXCA)) {
			mailingCityValidation = optionalCityValidation;
		}

		let mailingStateValidation = state_validation(labelSettings, 'mailing_country', addressType);
		if (isPerson && !(hasAnyAddressEntry && isUSMXCA)) {
			mailingStateValidation = Yup.string().nullable();
		}

		let mailingPostalCodeValidation = postal_code_validation(labelSettings, 'mailing_country', addressType);
		if (isPerson && !(hasAnyAddressEntry && isUSMXCA)) {
			mailingPostalCodeValidation = Yup.string().nullable();
		}

		let schema = Yup.object().shape({});
		const taxIdValidationBase = Yup.string().nullable().matches(RegularExpressions.SSN, labelSettings?.ssnInvalid || 'SSN is not valid.').required(getRequiredText(labelSettings.requiredFormat, labelSettings.ssnTinLabel));


		const taxIdValidation = taxIdValidationBase.test('taxid_valid_format',
			(value, context) => {
				const idStriped = replaceAll(value, '-', '');

				if (idStriped === '012345678' ||
					idStriped === '123456789') {
					return context.createError({ message: labelSettings?.ssnInvalid || 'SSN is not valid.' });
				}

				return true;
			});

		schema = schema.shape({
			relationship: beneficiary_type === BeneficiaryType.Person ? Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.relationshipLabel)) : null,
			disabled_child_indicator: Yup.string().nullable().ensure().when('relationship', {
				is: (relationship) => beneficiary_type === BeneficiaryType.Person && (relationship === RelationshipType.StepChild || relationship === RelationshipType.Child),
				then: Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings?.disabledChildLabel)),
			}),
			first_name: beneficiary_type === BeneficiaryType.Person ? Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.legalFirstNameLabel)) : null,
			last_name: beneficiary_type === BeneficiaryType.Person ? Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.legalLastNameLabel)) : null,
			gender: beneficiary_type === BeneficiaryType.Person ? Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.genderLabel)) : null,
			date_of_birth: beneficiary_type === BeneficiaryType.Person ? Yup.string().nullable().test('valid_format',
				(value, context) => {

					if (!value) return context.createError({ message: getRequiredText(labelSettings.requiredFormat, labelSettings.dobLabel) });

					const valueMoment = moment(value);

					if (!valueMoment.isValid()) return context.createError({ message: 'Invalid Format' });

					const nowMoment = moment();

					return valueMoment.diff(nowMoment) >= 0 ? context.createError({ message: labelSettings.dateFutureError || 'Date cannot be in the future' }) : true;
				}) : null,
			tax_id: taxIdValidation,
			personal_email_address: beneficiary_type === BeneficiaryType.Person ? Yup.string().nullable().email(labelSettings.emailFormatError).ensure() : null,
			mailing_country: mailingCountryValidation,
			mailing_address_1: mailingAddress1Validation,
			mailing_address_2: mailingAddress2Validation,
			mailing_address_3: address_3_validation(labelSettings),
			mailing_city: mailingCityValidation,
			mailing_state: mailingStateValidation,
			mailing_postal_code: mailingPostalCodeValidation,
			org_name: beneficiary_type !== BeneficiaryType.Person ? Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, GetOrgNameLabel(beneficiary_type, settings))) : null,
			contact_phone:
				beneficiary_type !== BeneficiaryType.Person ?
					Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.contactPhoneLabel)).matches(RegularExpressions.ContactPhone, settings.contactPhoneInvalidFormat || 'Invalid Contact Phone Number')
					: null,
			contact_first_name: beneficiary_type !== BeneficiaryType.Person ? Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.contactFistNameLabel)) : null,
			contact_last_name: beneficiary_type !== BeneficiaryType.Person ? Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.contactLastNameLabel)) : null,
			home_phone_formated: PhoneValidationSchema(
				getRequiredText(labelSettings?.requiredFormat, labelSettings?.homePhoneLabel),
				personPhoneValues?.home_phone_country_code,
				'home',
				false),
			home_phone_country_code: PhoneCountryCodeValidationSchema(
				getRequiredText(labelSettings?.requiredFormat, labelSettings?.homePhoneLabel),
				personPhoneValues?.home_phone_formated,
				false),
			mobile_phone_formated: PhoneValidationSchema(
				getRequiredText(labelSettings?.requiredFormat, labelSettings?.cellPhoneLabel),
				personPhoneValues?.mobile_phone_country_code,
				'mobile',
				false),
			mobile_phone_country_code: PhoneCountryCodeValidationSchema(
				getRequiredText(labelSettings?.requiredFormat, labelSettings?.cellPhoneLabel),
				personPhoneValues?.mobile_phone_formated,
				false),
			acknowledge: beneficiary_type !== BeneficiaryType.Person ?
				Yup.boolean().required(settings?.legalAcknowledgementTitle).oneOf([true], settings?.legalAcknowledgementTitle) :
				null,
		}, [['office_email_address', 'personal_email_address'], ['tax_id', 'date_of_birth']]);
		return schema;
	});
}

const OrgAddressMap = {
	physical_country: 'mailing_country',
	physical_postal_code: 'mailing_postal_code',
	physical_city: 'mailing_city',
	physical_state: 'mailing_state',
	physical_address_1: 'mailing_address_1',
	physical_address_2: 'mailing_address_2',
	physical_address_3: 'mailing_address_3',
};

export function MapBeneficiaryCreateApiError(errors) {
	const mappedErrors = {};
	
	Object.keys(errors).forEach((key) => {
			const mappedKey = OrgAddressMap[key] || key;
			mappedErrors[mappedKey] = errors[key];
	});
	
	return mappedErrors;
}


